import React, { ReactElement, useState } from "react";
import { ThemeProvider, Button, TextField } from "@material-ui/core";
import theme from "../../../themes";
import { useFormik } from "formik";
import * as yup from "yup";
import { makeStyles } from "@material-ui/core/styles";
import CircularProgress from "@material-ui/core/CircularProgress";
import { signInHandler } from "./SignInHandler";
import { getSignInErrorText } from "./authErrorText";
import Logo from "../../../images/Gold_Logo.png";
import { Link, useHistory } from "react-router-dom";
import { RedirectUri, useEnumStrings } from "../../enumStrings";
import PasswordField from "../../common/PasswordField";

interface LoginInput {
  email: string;
  password: string;
}
const useStyles = makeStyles((theme) => ({
  root: {
    "& .MuiTextField-root": {
      margin: theme.spacing(1),
      width: "25ch",
      marginLeft: "auto",
      marginRight: "auto",
    },
  },
  loginForm: {
    height: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  img: {
    width: "250px",
    display: "block",
    margin: "auto",
    paddingTop: "9.2px",
  },
  heading: {},
  formFields: {
    display: "flex",
    flexDirection: "column",
    maxWidth: "500px",
    height: "600px",
    background: "white",
    padding: "29px",
    boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
    position: "absolute",
    left: "0",
    right: "0",
    top: "0",
    bottom: "0",
    margin: "auto",
    textAlign: "center",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "center",
    flexDirection: "column",
    alignItems: "center",
    marginTop: "40px",
    paddingBottom: "9px",
  },
  textField: {
    backgroundColor: "white",
  },
  textFieldLabel: {
    fontSize: "18px",
  },
  formBody: {
    width: "90%",
    maxWidth: "585px",
  },
  invalidError: {
    textAlign: "center",
    color: "red",
  },
  icon: {
    margin: "auto",
    marginTop: "30px",
  },
}));

const Login = (): ReactElement => {
  const classes = useStyles();
  const enumStrings = useEnumStrings();
  const history = useHistory();
  const [invalidError, setInvalidError] = useState(false);
  const [customError, setCustomError] = useState("");
  const [loader, setLoader] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const initialValues: LoginInput = {
    email: "",
    password: "",
  };

  const validationSchema = yup.object({
    email: yup.string().required(enumStrings.loginValidation.emailRequired),
    password: yup
      .string()
      .required(enumStrings.loginValidation.passwordRequired),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (input: LoginInput) => {
      setLoader(true);
      try {
        await signInHandler(
          input.email.trim().toLowerCase(),
          input.password.trim(),
          () => history.push(RedirectUri.newPassword)
        );
        setButtonDisabled(true);
      } catch (error) {
        setInvalidError(true);
        setCustomError(getSignInErrorText(error, enumStrings.loginErrorMessages, "staff"));
        setLoader(false);
        setButtonDisabled(false);
      }
    },
  });

  return (
    <ThemeProvider theme={theme}>
      <form className={classes.formFields} onSubmit={formik.handleSubmit}>
        <img
          src={Logo}
          width="180px"
          height="180px"
          className={classes.icon}
          alt="capIcon"
        />
        <h1 style={{ fontWeight: "bold", color: "#E1A731" }}>{enumStrings.loginText.dashboardTitle}</h1>
        <div>
          {invalidError ? (
            <div className={classes.invalidError}>{customError}</div>
          ) : null}
        </div>{" "}
        <TextField
          variant="outlined"
          InputProps={{
            className: classes.textField,
          }}
          InputLabelProps={{
            className: classes.textFieldLabel,
          }}
          fullWidth
          id="email"
          name="email"
          label={enumStrings.loginText.emailLabel}
          value={formik.values.email}
          onChange={formik.handleChange}
          error={formik.touched.email && Boolean(formik.errors.email)}
          helperText={formik.touched.email && formik.errors.email}
        />
        <PasswordField
          id="password"
          fieldName="password"
          label={enumStrings.loginText.passwordLabel}
          value={formik.values.password}
          onChange={formik.handleChange}
          error={formik.errors.password}
          touched={formik.touched.password}
          disablePopup
          hideText
          showVisibilityEyeball
        />
        <div className={classes.buttonContainer}>
          <Button
            type="submit"
            size="large"
            color="primary"
            variant="contained"
            style={{ height: "70px" }}
            disabled={buttonDisabled || loader}
          >
            {loader ? <CircularProgress /> : enumStrings.loginText.loginButton}
          </Button>
        </div>
        <Link
          style={{
            textAlign: "center",
            color: "#E1A731",
            textDecoration: "underline",
            fontWeight: "bold",
          }}
          to={RedirectUri.forgotPassword}
        >
          {enumStrings.loginText.forgotPasswordText}
        </Link>
      </form>
    </ThemeProvider>
  );
};

export default Login;
